import { SSE } from 'sse.js';
import { ERROR_MESSAGE_FRIENDLY } from 'src/shared/constants';
import { refreshToken } from 'src/services/FetchRequest';
import { PATH_AUTH_SPA } from 'src/constants/spa-routes';
import { clearOnLogout } from 'src/utils/clear.util';
import { API_VERSION } from 'src/constants/api-routes';
import { getErrorMessage } from 'src/utils/get-error-message';

interface UserTokens {
  access?: string;
  access_token?: string;
  refresh?: string;
  refresh_token?: string;
}

interface SSECallbackPayload {
  message?: unknown;
  error?: string;
}

export const getAccessToken = (): string | null => {
  const raw = localStorage.getItem('user');
  if (!raw || raw === 'undefined') return null;

  const user: UserTokens = JSON.parse(raw);
  return user.access || user.access_token || null;
};

export const getRefreshToken = (): string | null => {
  const raw = localStorage.getItem('user');
  if (!raw || raw === 'undefined') return null;

  const user: UserTokens = JSON.parse(raw);
  return user.refresh || user.refresh_token || null;
};

const getSSEHeaders = (): Record<string, string> => {
  const access = getAccessToken();
  return {
    Authorization: `Bearer ${access}`,
    Accept: 'application/json, application/xml, text/plain, text/html, *.*',
    'Content-Type': 'application/json; charset=utf-8',
  };
};

let isRefreshing = false;

export const sendSSEMessage = (
  method: string,
  url: string,
  message: string,
  callback: (payload: SSECallbackPayload) => void = () => {},
): void => {
  const headers = getSSEHeaders();
  const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;
  const payload = JSON.stringify(
    version === API_VERSION.V1 ? [{ body: message }] : { body: message },
  );

  const source = new SSE(url, {
    method,
    headers,
    payload,
    reconnectDelay: 3000,
    maxRetries: 5,
  });

  source.addEventListener('abort', () => {
    callback({
      error: ERROR_MESSAGE_FRIENDLY,
    });
  });

  source.addEventListener('message', (e: MessageEvent) => {
    try {
      const message = JSON.parse(e.data);
      callback({ message });
    } catch (error) {
      callback({ error: 'There was an error while parsing message from AI.' });
    }
  });

  source.addEventListener('error', async (e: { responseCode?: number; data?: any }) => {
    if (e.responseCode === 401) {
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const refresh_token = getRefreshToken();
          const token = await refreshToken(refresh_token);

          if (token.error?.status === 401) {
            clearOnLogout();
            isRefreshing = false;
            window.location.assign(PATH_AUTH_SPA.ROOT);
          }

          if (token.access_token) {
            isRefreshing = false;
            const newUser = {
              refresh_token,
              access_token: token.access_token,
            };
            localStorage.setItem('user', JSON.stringify(newUser));
            return sendSSEMessage(method, url, message, callback);
          } else {
            isRefreshing = false;
            localStorage.removeItem('user');
            window.location.assign(PATH_AUTH_SPA.ROOT);
            return token;
          }
        } catch (error) {
          isRefreshing = false;
          return console.log('Refresh token error', error);
        }
      }
    }

    if (source.maxRetries && source.retryCount >= source.maxRetries) {
      console.log('Max retries reached, connection permanently closed');
    }

    if (e.data) {
      try {
        const jsonErrorData = JSON.parse(e.data);
        const errorMessage = getErrorMessage({ data: jsonErrorData });

        callback({ error: errorMessage });
      } catch (error) {
        callback({ error });
      }
    }
  });
};
